import { Pressable, StyleSheet, View } from "react-native";
import { AlertTriangle, X } from "lucide-react";
import CustomText from "../../common/general/CustomText/CustomText";
import { colors, spacing, typographyScale } from "../../../styles/design-system";

interface ConnectorReauthenticationBannerProps {
  issueCount: number;
  onDismiss?: () => void;
}

const getBannerText = (issueCount: number) =>
  issueCount === 1 ? "1 service needs attention" : `${issueCount} services need attention`;

export function ConnectorReauthenticationBanner({ issueCount, onDismiss }: ConnectorReauthenticationBannerProps) {
  if (issueCount <= 0) {
    return null;
  }

  const bannerText = getBannerText(issueCount);

  return (
    <View accessibilityRole="alert" accessibilityLabel={bannerText} style={styles.banner}>
      <View style={styles.bannerContent}>
        <AlertTriangle size={18} color={colors.warning[600]} />
        <CustomText text={bannerText} useTranslationText={false} style={styles.bannerText} />
      </View>
      {onDismiss ? (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Dismiss connection alert"
          onPress={onDismiss}
          hitSlop={8}
          style={styles.dismissButton}>
          <X size={16} color={colors.warning[700]} />
        </Pressable>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  banner: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: spacing.md,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: colors.warning[200],
    backgroundColor: colors.warning[25],
  },
  bannerContent: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.sm,
  },
  bannerText: {
    flex: 1,
    color: colors.warning[700],
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
  },
  dismissButton: {
    width: 28,
    height: 28,
    alignItems: "center",
    justifyContent: "center",
  },
});

export type { ConnectorReauthenticationBannerProps };
